import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC } from 'react';
// Components
import { Cancel, CheckCircle, Edit, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  IconButton,
  Theme,
  Typography
} from '@mui/material';
// Types
import { IPeople } from '../../../models';

interface IMobileExternalUser {
  original: IPeople;
  handleEdit: (val: IPeople) => void;
  handleExternalEdit: (val: any) => void;
  handleExternalDelete: (val: string | number) => void;
  handleExternalActivation: (val: string | number) => void;
  isVendorAffiliate?: boolean;
}

export const MobileExternalUser: FC<IMobileExternalUser> = ({
  original,
  handleEdit,
  handleExternalEdit,
  handleExternalDelete,
  handleExternalActivation,
  isVendorAffiliate
}) => {
  const classes = useStyles();
  const { firstName, lastName, workplace, email, type, partners, locations } = original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            {firstName} {lastName}
          </Typography>
          <div className={classes.buttonsWrapper}>
            <IconButton
              className={classes.editButton}
              color="primary"
              onClick={e => {
                e.stopPropagation();
                handleExternalEdit(original);
              }}
            >
              <Edit />
            </IconButton>
            {handleExternalDelete && original.isActive && (
              <IconButton
                className={classes.deleteButton}
                title="Deactivate User"
                color="error"
                onClick={e => {
                  e.stopPropagation();
                  handleExternalDelete(original.personId);
                }}
                size="large"
              >
                <Cancel />
              </IconButton>
            )}
            {handleExternalActivation && !original.isActive && (
              <IconButton
                className={classes.activateButton}
                title="Reactivate User"
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleExternalActivation(original.personId);
                }}
                size="large"
              >
                <CheckCircle />
              </IconButton>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            First Name:
          </FormLabel>
          <Typography component="span" variant="body2">
            {firstName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Last Name:
          </FormLabel>
          <Typography component="span" variant="body2">
            {lastName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Email:
          </FormLabel>
          <Typography component="span" variant="body2">
            {email || '--'}
          </Typography>
        </div>

        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Company:
          </FormLabel>
          <Typography component="span" variant="body2">
            {partners?.length > 0 ? partners?.map(p => p.partnerName)?.join(', ') : workplace ?? ''}
          </Typography>
        </div>
        {!isVendorAffiliate && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Person Type:
            </FormLabel>
            <Typography component="span" variant="body2">
              {type || '--'}
            </Typography>
          </div>
        )}
        {!isVendorAffiliate && (
          <div className={classes.panelSummaryWrapper}>
            <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
              Locations:
            </FormLabel>
            <Typography component="span" variant="body2">
              {locations || '--'}
            </Typography>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  activateButton: {
    padding: `2px 5px`
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  }
}));
